import { initializeApp, getApps, getApp } from 'firebase/app';
import { getFirestore, doc, setDoc, serverTimestamp, runTransaction } from 'firebase/firestore';
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut } from 'firebase/auth';
import config from '../../firebase-applet-config.json';

const firebaseConfig = {
  projectId: config.projectId,
  appId: config.appId,
  apiKey: config.apiKey,
  authDomain: config.authDomain,
  storageBucket: config.storageBucket,
  messagingSenderId: config.messagingSenderId,
  measurementId: config.measurementId,
};

const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);

function initFirestoreInstance() {
  const dbId = (config as any).firestoreDatabaseId;
  if (dbId && dbId !== '(default)') {
    try {
      return getFirestore(app, dbId);
    } catch (err) {
      console.warn('[Firebase] Custom databaseId initialization failed, falling back to default database:', err);
    }
  }
  try {
    return getFirestore(app);
  } catch (err) {
    console.warn('[Firebase] Firestore default database initialization warning:', err);
    return getFirestore(app);
  }
}

export const db = initFirestoreInstance();
export const auth = getAuth(app);

export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

export async function loginWithGoogle() {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    return result.user;
  } catch (error) {
    console.error('Google Auth Login Failed:', error);
    throw error;
  }
}

export async function logoutUser() {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Logout Failed:', error);
    throw error;
  }
}

export enum OperationType {
  CREATE = 'create',
  WRITE = 'write',
  GET = 'get',
  DELETE = 'delete'
}

export function handleFirestoreError(error: unknown, type: OperationType, path: string | null) {
  const info = {
    error: error instanceof Error ? error.message : String(error),
    operationType: type,
    path,
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
    }
  };
  console.error('Firestore Error:', JSON.stringify(info));
  throw new Error(JSON.stringify(info));
}

export async function createOrder(orderId: string, orderData: any) {
  const path = `orders/${orderId}`;
  try {
    const docRef = doc(db, 'orders', orderId);
    await setDoc(docRef, {
      ...orderData,
      createdAt: serverTimestamp()
    });
  } catch (error) {
    handleFirestoreError(error, OperationType.CREATE, path);
  }
}

export async function createOrderWithStockVerification(orderId: string, orderData: any) {
  const path = `orders/${orderId}`;

  // 0. Offline Check
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new Error('OFFLINE_ERROR');
  }

  try {
    await runTransaction(db, async (transaction) => {
      let calculatedSubtotal = 0;
      const verifiedItems: { ref: any; currentStock: number; currentSoldCount: number; isFlashSale: boolean; quantity: number }[] = [];
      
      // 1. Live verification of all ordered items from Firestore
      for (const item of orderData.items || []) {
        if (!item.productId) continue;
        const pRef = doc(db, 'products', item.productId);
        const pSnap = await transaction.get(pRef);
        
        if (!pSnap.exists() || pSnap.data().isDeleted) {
          throw new Error(`ITEM_UNAVAILABLE:${item.name}`);
        }

        const liveData = pSnap.data();
        const livePrice = (liveData.is_flash_sale && liveData.flashPrice) ? Number(liveData.flashPrice) : (Number(liveData.price) || 0);
        const currentStock = liveData.stock !== undefined ? Number(liveData.stock) : Number(liveData.stockQty ?? 0);
        const currentSoldCount = Number(liveData.soldCount || 0);
        const isFlashSale = Boolean(liveData.is_flash_sale);

        // Price drift check
        if (Math.abs(livePrice - Number(item.price)) > 0.01) {
          throw new Error(`PRICE_CHANGED:${item.name}:${livePrice}`);
        }

        // Stock availability check
        if (currentStock < Number(item.quantity)) {
          throw new Error(`OUT_OF_STOCK:${item.name}:${currentStock}`);
        }

        calculatedSubtotal += livePrice * Number(item.quantity);

        verifiedItems.push({
          ref: pRef,
          currentStock,
          currentSoldCount,
          isFlashSale,
          quantity: Number(item.quantity)
        });
      }

      // 2. Coupon Re-validation if coupon was applied
      let verifiedDiscount = 0;
      if (orderData.couponCode) {
        const couponRef = doc(db, 'store_info', 'coupons');
        const couponSnap = await transaction.get(couponRef);
        if (couponSnap.exists()) {
          const couponList = couponSnap.data()?.list || [];
          const activeCoupon = couponList.find((c: any) => 
            String(c.code).toUpperCase().trim() === String(orderData.couponCode).toUpperCase().trim() &&
            c.status === 'Active'
          );

          if (!activeCoupon) {
            throw new Error(`COUPON_INVALID:${orderData.couponCode}`);
          }

          if (activeCoupon.type === 'percentage') {
            verifiedDiscount = Math.round((calculatedSubtotal * Number(activeCoupon.value || 0)) / 100);
          } else {
            verifiedDiscount = Number(activeCoupon.value || 0);
          }
        }
      }

      // 3. Shipping Charge Re-validation
      let verifiedShipping = Number(orderData.deliveryFee) || 0;
      const shippingRef = doc(db, 'store_info', 'shipping');
      const shippingSnap = await transaction.get(shippingRef);
      if (shippingSnap.exists()) {
        const sData = shippingSnap.data();
        const minOrderForFree = Number(sData.min_order_for_global_free_delivery || 0);
        if (minOrderForFree > 0 && calculatedSubtotal >= minOrderForFree) {
          verifiedShipping = 0;
        }
      }

      // 4. Server Total Recalculation & Anti-Tampering Check
      const calculatedTotal = Math.max(0, calculatedSubtotal - verifiedDiscount + verifiedShipping);
      if (Math.abs(Number(orderData.total) - calculatedTotal) > 2) {
        throw new Error(`TOTAL_MISMATCH:${calculatedTotal}`);
      }

      // 5. Atomic stock deduction inside transaction
      for (const v of verifiedItems) {
        const remainingStock = Math.max(0, v.currentStock - v.quantity);
        const updateData: any = {
          stock: remainingStock,
          stockQty: remainingStock
        };
        if (v.isFlashSale) {
          updateData.soldCount = (v.currentSoldCount || 0) + v.quantity;
        }
        transaction.update(v.ref, updateData);
      }

      // 6. Write order document
      const orderDocRef = doc(db, 'orders', orderId);
      transaction.set(orderDocRef, {
        ...orderData,
        subtotal: calculatedSubtotal,
        discount: verifiedDiscount,
        deliveryFee: verifiedShipping,
        total: calculatedTotal,
        createdAt: serverTimestamp()
      });

      // 7. Update system catalog metadata
      const metaRef = doc(db, 'system', 'catalog_meta');
      transaction.set(metaRef, { updatedAt: Date.now() }, { merge: true });
    });
  } catch (error: any) {
    if (error?.message && (
      error.message === 'OFFLINE_ERROR' ||
      error.message.includes('PRICE_CHANGED') ||
      error.message.includes('OUT_OF_STOCK') ||
      error.message.includes('ITEM_UNAVAILABLE') ||
      error.message.includes('COUPON_INVALID') ||
      error.message.includes('TOTAL_MISMATCH')
    )) {
      throw error;
    }
    handleFirestoreError(error, OperationType.CREATE, path);
  }
}

export async function createSupportLead(leadId: string, leadData: any) {
  const path = `support_leads/${leadId}`;
  try {
    const docRef = doc(db, 'support_leads', leadId);
    await setDoc(docRef, {
      ...leadData,
      createdAt: serverTimestamp()
    });
  } catch (error) {
    handleFirestoreError(error, OperationType.CREATE, path);
  }
}

